import { call, DEFAULT_BASIS, GRID_STALE_TIME, PricingError, type Basis, type Channel, type CompetitorMax, type Market } from "./api";
import { CUSTOMER_COPY, servingCell, type CustomerCell, type ServingFigures } from "./customer";
import { fractionPct, type Programme } from "./discounts";
import { ABSENT, BASIS_LABEL, humanDate, messageFor, money, signedPct } from "./format";
import type { RowBands } from "./signal";
import type { ProjectedLine, ProjectedMargins, ProjectionMissing, ProjectionWeighting, VsAmazonUk, VsPrevious, VsRrp } from "./projection";
import { VIEWS_KEY, type StrategyBlock } from "./strategies";

// The order-type views of the Price Run (C1 v1.23, companion of precision-pricing `c2-pricing-views`, C2 v1.23, CL-029).
// One read per market and view answers every marketplace and store row at the net price of the view: the list price, the
// rate, the net, the projection's figures and the strategy's verdicts at that net. The browser shows C2's strings and works
// nothing out (H12): the net, the percentages and every margin are C2's; a row C2 does not view keeps its reason (INV-3).

/** The view on screen. Single order is no view: the grid at the list price, exactly as before (`pp_view` absent). */
export type ViewKey = "subscription" | `programme:${number}`;
export type ViewHeader = {
  kind: "subscription" | "programme"; label: string; programme_id: number | null; programme: string | null;
  basis: "unit" | "case" | null; base: "shopify" | "amazon" | "rrp" | null; default_rate: string | null; save_allowed: boolean;
};
/** Where a row's rate came from: a subscription entry (`kind` to `id`) or a programme rate (`resolved_from` to `base`). */
export type RateSource = {
  kind: string | null; marketplace_id: string | null; inherited: boolean | null; id: number | null; note: string | null;
  resolved_from: string | null; rate_id: number | null; base: string | null;
};
/** The projection's figures at the net price (C2 v1.13 shape less the identity keys and the notes). */
export type ViewFigures = {
  price: { value: string; currency: string; vat_inclusive: boolean }; net_price: string | null; fulfilment_total: string | null;
  margins: ProjectedMargins; weighting: ProjectionWeighting | null; lines: ProjectedLine[];
  metrics: { vs_previous_price_pct: VsPrevious; vs_amazon_uk_pct: VsAmazonUk; vs_rrp_pct: VsRrp; price_per_serving: ServingFigures; vs_competitor_max_pct?: CompetitorMax };
  missing: ProjectionMissing[];
};
/** One row of a view. Exactly one of three: `no_price`; `not_viewed` (the reason, verbatim); or the net with its figures. */
export type ViewRow = {
  xero_sku: string; marketplace_id: string; grain: "marketplace" | "store"; product: string; variant: string; channel: Channel;
  list_price: { value: string; currency: string; pending: boolean; line_id: number | null } | null; no_price: string | null;
  rate: string | null; rate_source: RateSource | null; net_from: { value: string; currency: string; source: "list_price" | "rrp" } | null;
  net_price: string | null; not_viewed: string | null; figures: ViewFigures | null; strategy: StrategyBlock | null;
  /** C2 per-row bands at the view's figures (c1-price-run-signal-rows, E2); absent on an older API. */
  bands?: RowBands;
};
export type PricingView = {
  market: Market; basis: Basis; as_of: string; view: ViewHeader; strategised: boolean; row_count: number; viewed_count: number;
  rows: ViewRow[]; method: string;
};

/** One view of one market on one basis, on a picked date when `asOf` is set (C2 v1.27 `&as_of=`): the figures and verdicts at the
 * fees in force on it. Today's URL is unchanged. */
export const getPricingView = (market: Market, view: ViewKey, basis: Basis = DEFAULT_BASIS, asOf: string | null = null) =>
  call<PricingView>(`/pricing-views/${market}?view=${encodeURIComponent(view)}${basis === DEFAULT_BASIS ? "" : `&basis=${basis}`}${asOf === null ? "" : `&as_of=${asOf}`}`);
/** One read per market, basis, view and date, kept as long as the grid. The key sits under the evaluation's, so every write that
 * re-reads the evaluation (a save, a remove, a commit, a strategy or Config edit) re-reads the views; a discount write adds it. A
 * picked date adds two parts to today's key (C1 v1.25), so the dated read is re-read by the same writes. */
export function viewQuery(market: Market, view: ViewKey, basis: Basis = DEFAULT_BASIS, asOf: string | null = null) {
  const queryKey = asOf === null ? [...VIEWS_KEY, market, basis, view] : [...VIEWS_KEY, market, basis, view, "as_of", asOf];
  return { queryKey, queryFn: () => getPricingView(market, view, basis, asOf), retry: 1, staleTime: GRID_STALE_TIME };
}

/** The `pp_view` URL value: `subscription`, or `programme:<id>` with a positive id; anything else is Single order. */
export function viewFromSearch(value: unknown): ViewKey | null {
  if (value === "subscription") return "subscription";
  return typeof value === "string" && /^programme:[1-9]\d*$/.test(value) ? (value as ViewKey) : null;
}
export const programmeView = (id: number): ViewKey => `programme:${id}`;
export const isPartnerView = (view: ViewKey | null) => view !== null && view !== "subscription";

export const ORDER_TYPE_COPY = {
  label: "Order type",
  single: "Single order",
  subscription: "Subscription",
  programmesUnread: "Partner programmes could not be read, so only Single order and Subscription are offered.",
  casesNote: (names: string) => `Per-case programmes are priced on the Discounts tab: ${names}.`,
  caption: (label: string, basis: Basis, asOf: string | null) => `Margins read ${label} · ${BASIS_LABEL[basis]} · ${asOf ? `as of ${humanDate(asOf)}` : "reading"}`,
  subscriptionBanner: (n: number) =>
    `Subscription view: every figure reads the price less the subscription discount.${n === 0 ? "" : ` ${n} ${n === 1 ? "row is" : "rows are"} not in this view; ${n === 1 ? "it names" : "each names"} why.`}`,
  partnerBanner: (name: string) => `Partner prices are set in Discounts. This view shows each row at the ${name} rate; saving a price is off here.`,
  failed: (label: string, market: string, reason: string) => `The ${label} view could not be read for ${market}, so its rows show Single order figures. ${reason}`,
  reading: "Reading the view",
  pays: (label: string, net: string, pct: string, from: string) => `${label} · ${net} after ${pct}% off ${from}`,
  fromList: (price: string, pending: boolean) => (pending ? `the saved ${price}` : price),
  fromRrp: (price: string) => `the RRP ${price}`,
  exVat: (price: string) => `${price} ex VAT`,
  lineExVat: (vat: string, price: string) => `VAT ${vat} · ex VAT ${price}`,
  notViewed: (label: string, reason: string) => `${label} · ${reason}`,
  notInView: "Not in this view",
  absent: "The pricing API does not list this row in the view",
  noPrice: (reason: string) => `No price in this view: ${reason}`,
  noFigures: "no figures in this view",
  judgedAt: (label: string, net: string) => `judged at the ${label} net ${net}`,
  vsLastList: "vs last set list price",
  listWas: (price: string) => `list ${price}`,
  vsAmazonUkList: "vs Amazon UK list price",
  vsRrp: "vs RRP",
  noCountry: "No figure for this country in the view",
  saveSubscription: (net: string, pct: string, list: string) =>
    `Subscription view: subscribers pay ${net}, ${pct}% off the list price ${list}. The price you save is the list price.`,
  saveNoOffer: (reason: string) => `Subscription view: ${reason}. The price you save is the list price.`,
  boxAtList: "The figures and rules below are at the typed list price, as Single order; the view's are in the block above.",
  detailTitle: (label: string) => `${label} view`,
  detailMargins: (gross: string, fulfil: string, fees: string) => `Gross ${gross} · Fulfil ${fulfil} · Fees ${fees}`,
  detailMissing: (names: string) => `Not worked out at the net: ${names}`,
};

/** The label of the view on screen: C2's once a read has answered, else the programme's name from the Discounts read. */
export function viewLabel(view: ViewKey | null, programmes: Programme[] | undefined, answers: Array<PricingView | null>): string {
  if (view === null) return ORDER_TYPE_COPY.single;
  const answered = answers.find((a) => a !== null);
  if (answered) return answered.view.label;
  if (view === "subscription") return ORDER_TYPE_COPY.subscription;
  return programmes?.find((p) => programmeView(p.id) === view)?.name ?? view.replace("programme:", "Programme ");
}

/** A failed view read in words: C2's own sentence when it sent one (`unknown_programme`, `bad_view`), else the Price Run's. */
export function viewRefusal(error: unknown, areaName: string): string {
  if (error instanceof PricingError && error.status !== 401 && error.status !== 403) {
    const message = error.facts().message;
    if (message) return message;
  }
  return messageFor(error, areaName);
}

export type ViewRead = { view: PricingView | null; error: unknown };
export type ViewReads = Partial<Record<Market, ViewRead>>;
/** A Price Run row in the view on screen. `off`: no view, or its read failed (the row shows the grid, named above the table). */
export type ViewLookup =
  | { kind: "off" } | { kind: "pending" } | { kind: "absent"; view: PricingView } | { kind: "row"; row: ViewRow; view: PricingView };
/** By SKU and place, as the evaluation is looked up; a ship-country row reads its store's. The view's rows are a superset of the
 * grid's (C2 lists every row of the strategy map), so a grid row missing from an answer is named, never guessed. */
export function viewLookup(reads: ViewReads | null, market: Market, sku: string, place: string): ViewLookup {
  const read = reads?.[market];
  if (!read || (!read.view && read.error)) return { kind: "off" };
  if (!read.view) return { kind: "pending" };
  const row = read.view.rows.find((r) => r.xero_sku === sku && r.marketplace_id === place);
  return row ? { kind: "row", row, view: read.view } : { kind: "absent", view: read.view };
}

/** A listed row the view prices: no `not_viewed` reason, and a net price with the price and the rate it came from. A row with a
 * missing price is judged before this (`no_price`). The one test behind the line under the price and the banner's count. */
type PricedRow = ViewRow & { net_price: string; net_from: NonNullable<ViewRow["net_from"]>; rate: string };
const priced = (row: ViewRow): row is PricedRow => !row.not_viewed && Boolean(row.net_price) && Boolean(row.net_from) && Boolean(row.rate);

/** The line under the price: what the view's customer pays, off which price (the saved line or the RRP named), or C2's reason. */
export function viewPriceLine(lookup: ViewLookup): { text: string; muted: boolean } | null {
  if (lookup.kind === "off") return null;
  if (lookup.kind === "pending") return { text: ORDER_TYPE_COPY.reading, muted: true };
  if (lookup.kind === "absent") return { text: ORDER_TYPE_COPY.absent, muted: true };
  const { row, view } = lookup;
  if (row.no_price) return { text: ORDER_TYPE_COPY.noPrice(row.no_price), muted: true };
  if (!priced(row)) return { text: ORDER_TYPE_COPY.notViewed(view.view.label, row.not_viewed ?? ABSENT), muted: true };
  const base = money(row.net_from.value, row.net_from.currency);
  const from = row.net_from.source === "rrp" ? ORDER_TYPE_COPY.fromRrp(base) : ORDER_TYPE_COPY.fromList(base, row.list_price?.pending ?? false);
  return { text: ORDER_TYPE_COPY.pays(view.view.label, money(row.net_price, row.net_from.currency), fractionPct(row.rate), from), muted: false };
}

/** The Customer column in a view (D3 of C2 v1.23): the comparisons are the net against references the view does not net, so
 * each is labelled against the list; the serving line is at the net. A row without figures says so. */
/** The Customer column's one figure in an order-type view (CLB-022): the view's move against the previous list price; null when
 * the view names none, `undefined` when no view is on. */
export function viewCustomerFigure(lookup: ViewLookup): string | null | undefined {
  if (lookup.kind === "off") return undefined;
  if (lookup.kind !== "row" || !lookup.row.figures) return null;
  const value = lookup.row.figures.metrics.vs_previous_price_pct.value;
  return value == null ? null : signedPct(value);
}
export function viewCustomerCell(lookup: ViewLookup): CustomerCell | null {
  if (lookup.kind === "off") return null;
  const empty = (detail: string): CustomerCell => ({ previous: ORDER_TYPE_COPY.vsLastList, detail, amazonUk: null, amazonUkTitle: null, serving: null, servingTitle: null, pending: null });
  if (lookup.kind === "pending") return empty(ORDER_TYPE_COPY.reading);
  if (lookup.kind === "absent" || !lookup.row.figures) return empty(ORDER_TYPE_COPY.noFigures);
  const { row } = lookup;
  const f = lookup.row.figures;
  const prev = f.metrics.vs_previous_price_pct;
  const uk = f.metrics.vs_amazon_uk_pct;
  const anchor = uk.anchor_sku === row.xero_sku;
  return {
    previous: prev.value == null ? ORDER_TYPE_COPY.vsLastList : `${ORDER_TYPE_COPY.vsLastList} ${signedPct(prev.value)}`,
    detail: prev.value == null ? prev.reason : ORDER_TYPE_COPY.listWas(money(prev.previous_price, f.price.currency)),
    amazonUk: `${ORDER_TYPE_COPY.vsAmazonUkList} ${anchor || uk.value == null ? CUSTOMER_COPY.na : signedPct(uk.value)}`,
    amazonUkTitle: anchor ? CUSTOMER_COPY.anchorRow : uk.value == null ? uk.reason : null,
    ...servingCell(f.metrics.price_per_serving),
    pending: null,
  };
}

/** The figures a Price Run row shows in the view: the row's own, or a ship country's line of its store's figures. */
export function viewMargins(figures: ViewFigures, location: string | null, child: boolean): { margins: ProjectedMargins; missing: ProjectionMissing[]; line: ProjectedLine | null } | null {
  if (!child) return { margins: figures.margins, missing: figures.missing, line: null };
  const line = figures.lines.find((l) => l.location === location);
  return line ? { margins: line.margins, missing: line.missing, line } : null;
}

/** The drawer's three comparisons in a view, each against the list reference C2 compares the net with (D3), with C2's reason
 * when it cannot be made; the Amazon UK row is its own anchor and says so. */
export function viewComparisons(f: ViewFigures, sku: string): string {
  const { vs_previous_price_pct: prev, vs_amazon_uk_pct: uk, vs_rrp_pct: rrp } = f.metrics;
  const part = (label: string, value: string | null, reason: string | null) => `${label} ${value == null ? `${ABSENT} (${reason ?? ABSENT})` : signedPct(value)}`;
  const amazonUk = uk.anchor_sku === sku ? `${ORDER_TYPE_COPY.vsAmazonUkList} ${ABSENT} (${CUSTOMER_COPY.anchorRow})` : part(ORDER_TYPE_COPY.vsAmazonUkList, uk.value, uk.reason);
  return [part(ORDER_TYPE_COPY.vsLastList, prev.value, prev.reason), amazonUk, part(ORDER_TYPE_COPY.vsRrp, rrp.value, rrp.reason)].join(" · ");
}

/** The New price box's line in the Subscription view (C2 v1.23 Handoff, review round 1): the list price being set beside the net
 * it implies, both C2's, at the saved or committed list price; a row without an offer says so. Nothing while unread. */
export function saveViewLine(lookup: ViewLookup): string | null {
  if (lookup.kind !== "row") return null;
  const r = lookup.row;
  if (r.net_price && r.rate && r.list_price) return ORDER_TYPE_COPY.saveSubscription(money(r.net_price, r.list_price.currency), fractionPct(r.rate), money(r.list_price.value, r.list_price.currency));
  return r.not_viewed ? ORDER_TYPE_COPY.saveNoOffer(r.not_viewed) : null;
}

/** How many rows the view on screen leaves out (C1 v1.35): a row C2 does not list in the view, or one it lists without a net
 * price for a reason other than a missing price. The line under the price uses the same test (`priced`); counted, never judged. */
export function notViewedCount(lookups: ViewLookup[]): number {
  return lookups.filter((l) => l.kind === "absent" || (l.kind === "row" && !l.row.no_price && !priced(l.row))).length;
}
